import { Component, Element, Event, EventEmitter, h, Host, Prop, Watch } from "@stencil/core";
import { getUniqueId } from "../../utils/unique-id";
import { UxOption } from "./option";

@Component({
    tag: "ux-select",
    styleUrl: "select.scss",
    shadow: false
})
export class Select {
    @Element() selectElement!: HTMLUxSelectElement;

    /** array of options */
    @Prop() readonly options: UxOption[] = [];
    /** label text describing the select component field */
    @Prop() readonly label: string = "";
    /** additional info label for the input field */
    @Prop() readonly labelInfo: string = "";
    /** select id attribute */
    @Prop({ reflect: true, mutable: true }) selectId = "";
    /** name of the select field */
    @Prop() readonly name: string = "";
    /** indicates if the select component is disabled */
    @Prop() readonly disabled: boolean = false;
    /** indicates if the select component is required  */
    @Prop() readonly required: boolean = false;
    /** indicates if the select component has an error state  */
    @Prop() readonly error: boolean = false;
    /** Error message that is rendered if component has an error */
    @Prop() readonly errorMsg: string = "";
    /** autocomplete attribute, will be set on the select element. Refer to HTML standards for allowed values */
    // @ts-expect-error Check for typing
    @Prop() readonly autocomplete: string = null;
    /** will make the select element extend for the full width of the parent element */
    @Prop() readonly fullWidth: boolean = false;

    @Watch("selectId")
    setSelectId() {
        this.selectId = this.selectId || getUniqueId();
    }

    componentWillLoad() {
        this.setSelectId();
        const firstOption = this.selectElement.querySelector("option");
        if (!firstOption) {
            return;
        }
        const optionSelected = this.selectElement.querySelector("option[selected]");
        if (!optionSelected) {
            firstOption.setAttribute("selected", "");
        }
    }

    /**
     * @deprecated use uxChange instead
     * event to set the currently active option value
     */
    @Event({
        eventName: "uxSelectChange",
        composed: true,
        cancelable: true,
        bubbles: true
    })
    changeEventDetail!: EventEmitter;

    /** event to handle change events from select */
    @Event({
        eventName: "uxChange",
        composed: true,
        cancelable: true,
        bubbles: true
    })
    changeEvent!: EventEmitter<{ value: string; selectedOption: HTMLOptionElement }>;

    /** function to emit new active option value and selected option */
    /* eslint-disable-next-line */
    private selectChange(newValue: any) {
        this.changeEventDetail.emit(newValue.value);
        this.changeEvent.emit({
            value: newValue.value,
            selectedOption: newValue.options[newValue.selectedIndex]
        });
    }

    render() {
        const { label, labelInfo, selectId, name, disabled, required, error, errorMsg, fullWidth } = this;

        return (
            <Host
                class={{
                    ["ux-select__wrapper"]: true,
                    ["ux-select__wrapper--full-width"]: fullWidth
                }}
            >
                <ux-input-label
                    text={label}
                    labelClasses="ux-select__wrapper__label"
                    inputId={selectId}
                    error={error}
                    errorMsg={errorMsg}
                    required={required}
                    labelInfo={labelInfo}
                />
                <div class={`ux-select__wrapper__inner`}>
                    <div
                        class={{
                            [`ux-select`]: true,
                            [`ux-select--disabled`]: disabled,
                            [`ux-select--error`]: error
                        }}
                    >
                        <ux-icon class="ux-select__icon" name="chevron-down" weight="bold" />
                        <select
                            class="ux-select__select"
                            onChange={(e) => this.selectChange(e.target)}
                            id={selectId}
                            name={name}
                            disabled={disabled}
                            required={required}
                            aria-invalid={error ? "true" : "false"}
                            autocomplete={this.autocomplete}
                        >
                            {this.options.map((option) => (
                                <option selected={!!option.selected} value={option.value} disabled={!!option.disabled}>
                                    {option.name}
                                </option>
                            ))}
                            <slot />
                        </select>
                    </div>
                </div>
            </Host>
        );
    }
}
